import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Form, Input, Button, Typography, Layout, Modal, Space, Grid, Divider } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, KeyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { appVersion } from '../../../config/config';
import { useNavigate, useLocation } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import { useAuthApi } from '../../../api/auth/auth';
import { useSignupApi } from '../../../api/auth/signup';
import GoogleSignInButton from '../signup/GoogleSignInButton';
import LanguageSwitcher from '../../../components/auth/LanguageSwitcher';

const { Title, Text } = Typography;
const { Content, Footer } = Layout;

interface LoginFormValues {
    username: string;
    password: string;
    remember: boolean;
}

const Login: React.FC = () => {
    const { t } = useTranslation();
    const { login, refreshMe } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fpVisible, setFpVisible] = useState<boolean>(false);
    const [fpLoading, setFpLoading] = useState<boolean>(false);
    const [identifier, setIdentifier] = useState<string>("");
    const [focusedField, setFocusedField] = useState<'username' | 'password' | null>(null);
    const [activeTab, setActiveTab] = useState<'login' | 'register'>(
        location.pathname === '/register' ? 'register' : 'login'
    );
    const [accountInactive, setAccountInactive] = useState<boolean>(false);
    const [resendVisible, setResendVisible] = useState<boolean>(false);
    const [resendEmail, setResendEmail] = useState<string>("");
    const [resendLoading, setResendLoading] = useState<boolean>(false);
    const { message } = AntdApp.useApp();
    const { requestPasswordReset, resendConfirmationEmail } = useAuthApi();
    const signupApi = useSignupApi();
    const [googleClientId, setGoogleClientId] = useState<string>('');

    // Le client OAuth vient du serveur : pas de rebuild du front par environnement.
    useEffect(() => {
        let cancelled = false;
        signupApi
            .getConfig()
            .then((cfg) => {
                if (!cancelled && cfg.google_enabled) setGoogleClientId(cfg.google_client_id);
            })
            .catch(() => {
                /* Google indisponible : la connexion par mot de passe suffit. */
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGoogleLogin = useCallback(
        async (idToken: string) => {
            setIsLoading(true);
            try {
                await signupApi.loginWithGoogle(idToken);
                await refreshMe();
                navigate('/');
            } catch (err: any) {
                const status = err?.response?.status;
                if (status === 404) {
                    message.error(
                        t('signup.errors.googleNoAccount', "Aucun compte n'est associé à ce compte Google. Créez-en un.")
                    );
                } else if (err?.response?.data?.code === 'account_inactive') {
                    setAccountInactive(true);
                } else {
                    message.error(t('signup.errors.googleLogin', 'La connexion avec Google a échoué.'));
                }
            } finally {
                setIsLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [signupApi, refreshMe, navigate, message, t]
    );

    // Filter out Arabic characters (Unicode range \u0600-\u06FF)
    const filterArabicCharacters = (value: string): string => {
        return value.replace(/[\u0600-\u06FF]/g, '');
    };

    const palette = useMemo(
        () => ({
            primary: '#2D9CC9',
            secondary: '#8BBE1E',
            dark: '#031323',
            background: '#f8fafc',
            surface: '#ffffff',
            border: '#e2e8f0',
            textPrimary: '#0f172a',
            textSecondary: '#64748b',
            textMuted: '#94a3b8',
        }),
        []
    );

    const screens = Grid.useBreakpoint();
    const isCompact = screens.md !== true;

    const onFinish = (values: LoginFormValues) => {
        setIsLoading(true);
        setAccountInactive(false);
        const { username, password } = values;

        (async () => {
            try {
                const ok = await login(username, password);
                if (ok) {
                    navigate('/');
                } else {
                    message.error(t('auth.login.invalidCredentials'));
                }
            } catch (err: any) {
                const code = err?.response?.data?.code;
                if (code === 'account_inactive') {
                    setAccountInactive(true);
                    // Pré-remplir l'email du modal avec le login saisi (si c'est un email)
                    if (username.includes('@')) setResendEmail(username);
                } else {
                    message.error(err?.response?.data?.message ?? err?.message ?? t('auth.login.connectionError'));
                }
            } finally {
                setIsLoading(false);
            }
        })();
    };

    const handleResendSubmit = async () => {
        if (!resendEmail.trim()) {
            message.warning('Veuillez saisir votre adresse email.');
            return;
        }
        setResendLoading(true);
        try {
            await resendConfirmationEmail(resendEmail.trim());
            message.success("Si un compte existe avec cet email, un nouveau lien d'activation a été envoyé. Vérifiez votre boîte de réception.");
            setResendVisible(false);
            setResendEmail("");
        } catch {
            message.success("Si un compte existe avec cet email, un nouveau lien d'activation a été envoyé. Vérifiez votre boîte de réception.");
            setResendVisible(false);
            setResendEmail("");
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <Layout
            style={{
                minHeight: '100vh',
                position: 'relative',
                background: palette.dark,
                padding: isCompact ? 'max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) 24px max(12px, env(safe-area-inset-left))' : '40px',
                paddingTop: isCompact
                    ? 'max(52px, calc(env(safe-area-inset-top) + 40px))'
                    : undefined,
            }}
        >
            {/* Language Switcher - Top Right */}
            <div
                style={{
                    position: 'absolute',
                    top: isCompact ? 'max(12px, env(safe-area-inset-top))' : 40,
                    right: isCompact ? 'max(12px, env(safe-area-inset-right))' : 40,
                    zIndex: 1000,
                }}
            >
                <LanguageSwitcher variant="dark" />
            </div>

            <Content
                style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: isCompact ? 'flex-start' : 'center',
                    position: 'relative',
                    maxWidth: '1200px',
                    width: '100%',
                    margin: '0 auto',
                    paddingTop: isCompact ? 8 : 0,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: '1100px',
                        backgroundColor: palette.surface,
                        borderRadius: isCompact ? 16 : 24,
                        overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        border: `1px solid rgba(255, 255, 255, 0.1)`,
                        display: 'flex',
                        flexDirection: isCompact ? 'column' : 'row',
                        minHeight: isCompact ? undefined : 600,
                    }}
                >
                    {/* Left Column - Abstract Background */}
                    <div
                        style={{
                            width: isCompact ? '100%' : '40%',
                            flexShrink: 0,
                            minHeight: isCompact ? 200 : undefined,
                            position: 'relative',
                            background: `linear-gradient(135deg, 
                                rgba(45, 156, 201, 0.4) 0%, 
                                rgba(45, 156, 201, 0.6) 25%,
                                rgba(139, 190, 30, 0.5) 75%,
                                rgba(139, 190, 30, 0.3) 100%)`,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Geometric Shapes Background */}
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: `
                                    polygon(0% 0%, 100% 0%, 85% 40%, 100% 70%, 60% 100%, 0% 80%, 20% 50%),
                                    polygon(40% 20%, 100% 10%, 95% 60%, 80% 90%, 20% 100%, 0% 70%, 10% 30%)
                                `,
                                backgroundSize: '200% 200%',
                                backgroundPosition: '0% 0%, 100% 100%',
                                mixBlendMode: 'multiply',
                                opacity: 0.3,
                            }}
                        />

                        {/* Overlay Geometric Patterns */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                background: `
                                    repeating-linear-gradient(45deg, 
                                        transparent, 
                                        transparent 20px, 
                                        rgba(255, 255, 255, 0.05) 20px, 
                                        rgba(255, 255, 255, 0.05) 40px)
                                `,
                                opacity: 0.5,
                            }}
                        />

                        {/* Content on Left Side */}
                        <div
                            style={{
                                position: 'relative',
                                zIndex: 2,
                                padding: isCompact ? '20px 16px 24px' : '48px 40px',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: isCompact ? 'flex-start' : 'space-between',
                                alignItems: 'center',
                                gap: isCompact ? 16 : 0,
                            }}
                        >
                            {/* Logo Section */}
                            <div style={{ width: '100%', textAlign: 'center', marginTop: isCompact ? 0 : 20 }}>
                                <img
                                    src="/images/logo_120x64.png"
                                    alt="Tilmicode"
                                    style={{
                                        height: isCompact ? 56 : 80,
                                        filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))',
                                        marginBottom: '8px',
                                    }}
                                />
                            </div>

                            <div style={{ width: '100%', textAlign: 'center' }}>
                                {/* Login Tab Button */}
                                <div
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={activeTab === 'login'}
                                    onClick={() => setActiveTab('login')}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveTab('login'); }}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 44,
                                        backgroundColor: activeTab === 'login' ? palette.surface : 'transparent',
                                        color: activeTab === 'login' ? palette.dark : palette.surface,
                                        padding: isCompact ? '10px 18px' : '12px 32px',
                                        borderRadius: '8px',
                                        fontSize: isCompact ? 14 : 16,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        marginBottom: '16px',
                                        transition: 'all 0.3s ease',
                                        border: `2px solid ${activeTab === 'login' ? palette.surface : 'transparent'}`,
                                        opacity: activeTab === 'login' ? 1 : 0.8,
                                        transform: activeTab === 'login' && !isCompact ? 'scale(1.05)' : 'scale(1)',
                                        outline: 'none',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (activeTab !== 'login') {
                                            e.currentTarget.style.opacity = '1';
                                            e.currentTarget.style.transform = isCompact ? 'scale(1)' : 'scale(1.02)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (activeTab !== 'login') {
                                            e.currentTarget.style.opacity = '0.8';
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }
                                    }}
                                    onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,255,255,0.4)'; }}
                                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    LOGIN
                                </div>

                                {/* Sign In/Register Tab */}
                                <div
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={activeTab === 'register'}
                                    onClick={() => { setActiveTab('register'); navigate('/register'); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setActiveTab('register'); navigate('/register'); } }}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 44,
                                        backgroundColor: activeTab === 'register' ? palette.surface : 'transparent',
                                        color: activeTab === 'register' ? palette.dark : palette.surface,
                                        padding: isCompact ? '10px 18px' : '12px 32px',
                                        borderRadius: '8px',
                                        fontSize: isCompact ? 14 : 16,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        border: `2px solid ${activeTab === 'register' ? palette.surface : 'transparent'}`,
                                        opacity: activeTab === 'register' ? 1 : 0.8,
                                        transform: activeTab === 'register' && !isCompact ? 'scale(1.05)' : 'scale(1)',
                                        outline: 'none',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (activeTab !== 'register') {
                                            e.currentTarget.style.opacity = '1';
                                            e.currentTarget.style.transform = isCompact ? 'scale(1)' : 'scale(1.02)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (activeTab !== 'register') {
                                            e.currentTarget.style.opacity = '0.8';
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }
                                    }}
                                    onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,255,255,0.4)'; }}
                                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    SIGN IN
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Login Form */}
                    <div
                        style={{
                            width: isCompact ? '100%' : '60%',
                            flex: isCompact ? undefined : 1,
                            minWidth: 0,
                            backgroundColor: palette.surface,
                            padding: isCompact ? '24px 20px 28px' : '48px 60px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}
                    >
                        {/* Header with Icon */}
                        <div style={{ textAlign: 'center', marginBottom: isCompact ? 28 : 40 }}>
                            <div
                                style={{
                                    width: isCompact ? 64 : 80,
                                    height: isCompact ? 64 : 80,
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: isCompact ? 16 : 24,
                                    boxShadow: `0 8px 24px rgba(45, 156, 201, 0.3)`,
                                }}
                            >
                                <UserOutlined style={{ fontSize: isCompact ? 28 : 36, color: palette.surface }} />
                            </div>
                            <Title
                                level={1}
                                style={{
                                    margin: 0,
                                    color: palette.primary,
                                    fontSize: isCompact ? 22 : 32,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: isCompact ? 1 : 2,
                                    lineHeight: 1.2,
                                }}
                            >
                                {t('auth.login.title')}
                            </Title>
                        </div>

                        {/* Form Section */}
                        <Form
                            name="login_form"
                            initialValues={{ remember: true }}
                            onFinish={onFinish}
                            layout="vertical"
                            requiredMark={false}
                            size="large"
                        >
                            <Form.Item
                                name="username"
                                rules={[
                                    { required: true, message: t('auth.login.usernameRequired') },
                                    {
                                        validator: (_, value) => {
                                            if (value && /[\u0600-\u06FF]/.test(value)) {
                                                return Promise.reject(new Error(t('auth.validation.noArabicCharacters')));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                                getValueFromEvent={(e) => {
                                    const filtered = filterArabicCharacters(e.target.value);
                                    if (filtered !== e.target.value) {
                                        message.warning(t('auth.validation.noArabicCharacters'));
                                    }
                                    return filtered;
                                }}
                                normalize={(value) => filterArabicCharacters(value || '')}
                                style={{ marginBottom: '32px' }}
                            >
                                <div>
                                    <Text
                                        style={{
                                            color: palette.textPrimary,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            display: 'block',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        {t('auth.login.usernamePlaceholder')}
                                    </Text>
                                    <div
                                        style={{
                                            borderBottom: `2px solid ${focusedField === 'username' ? palette.primary : palette.border}`,
                                            transition: 'border-color 0.2s ease',
                                        }}
                                    >
                                        <Input
                                            prefix={<UserOutlined style={{ color: palette.textMuted }} />}
                                            placeholder={t('auth.login.usernamePlaceholder')}
                                            bordered={false}
                                            style={{
                                                borderRadius: 0,
                                                padding: '8px 4px',
                                                fontSize: '15px',
                                            }}
                                            onFocus={() => setFocusedField('username')}
                                            onBlur={() => setFocusedField(null)}
                                        />
                                    </div>
                                </div>
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[{ required: true, message: t('auth.login.passwordRequired') }]}
                                style={{ marginBottom: '24px' }}
                            >
                                <div>
                                    <Text
                                        style={{
                                            color: palette.textPrimary,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            display: 'block',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        {t('auth.login.passwordPlaceholder')}
                                    </Text>
                                    <div
                                        style={{
                                            borderBottom: `2px solid ${focusedField === 'password' ? palette.primary : palette.border}`,
                                            transition: 'border-color 0.2s ease',
                                        }}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined style={{ color: palette.textMuted }} />}
                                            placeholder={t('auth.login.passwordPlaceholder')}
                                            bordered={false}
                                            style={{
                                                borderRadius: 0,
                                                padding: '8px 4px',
                                                fontSize: '15px',
                                            }}
                                            onFocus={() => setFocusedField('password')}
                                            onBlur={() => setFocusedField(null)}
                                        />
                                    </div>
                                </div>
                            </Form.Item>

                            {/* Forgot Password Link */}
                            <div style={{ textAlign: 'right', marginBottom: '32px' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFpVisible(true);
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        color: palette.secondary,
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        textDecoration: 'none',
                                        transition: 'all 0.2s',
                                        cursor: 'pointer',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.textDecoration = 'underline';
                                        e.currentTarget.style.color = palette.primary;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.textDecoration = 'none';
                                        e.currentTarget.style.color = palette.secondary;
                                    }}
                                >
                                    {t('auth.login.forgotPassword')}
                                </button>
                            </div>

                            {/* Login Button */}
                            <Form.Item style={{ marginBottom: '32px' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="large"
                                    block
                                    loading={isLoading}
                                    style={{
                                        borderRadius: '12px',
                                        height: '52px',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        border: 'none',
                                        background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                                        boxShadow: `0 4px 12px rgba(45, 156, 201, 0.3)`,
                                        transition: 'all 0.3s',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = `0 6px 20px rgba(45, 156, 201, 0.4)`;
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = `0 4px 12px rgba(45, 156, 201, 0.3)`;
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    {isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
                                </Button>
                            </Form.Item>

                            {/* Connexion Google — indispensable pour les comptes créés via
                                Google, qui n'ont pas de mot de passe. */}
                            {googleClientId && (
                                <div style={{ marginBottom: 24 }}>
                                    <Divider plain style={{ color: palette.textMuted, fontSize: 13 }}>
                                        {t('signup.account.or', 'ou')}
                                    </Divider>
                                    <GoogleSignInButton
                                        clientId={googleClientId}
                                        text="signin_with"
                                        onCredential={handleGoogleLogin}
                                    />
                                </div>
                            )}

                            {/* Compte inactif — lien resend */}
                            {accountInactive && (
                                <div style={{
                                    marginBottom: '16px',
                                    padding: '12px 16px',
                                    borderRadius: '10px',
                                    background: '#fff7e6',
                                    border: '1px solid #ffd591',
                                    textAlign: 'center',
                                }}>
                                    <Text style={{ color: '#d46b08', fontSize: '14px' }}>
                                        Votre compte n'est pas activé.{' '}
                                        <button
                                            type="button"
                                            onClick={() => setResendVisible(true)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                                color: palette.primary,
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            Renvoyer l'email d'activation
                                        </button>
                                    </Text>
                                </div>
                            )}

                            {/* Register Link */}
                            <div style={{ textAlign: 'center', paddingTop: '24px', borderTop: `1px solid ${palette.border}` }}>
                                <Text style={{ color: palette.textSecondary, fontSize: '14px' }}>
                                    {t('auth.login.noAccount')}{' '}
                                    <a
                                        href="/register"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate('/register');
                                        }}
                                        style={{
                                            color: palette.primary,
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.textDecoration = 'underline';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.textDecoration = 'none';
                                        }}
                                    >
                                        {t('auth.login.createAccount')}
                                    </a>
                                </Text>
                            </div>
                        </Form>
                    </div>
                </div>
            </Content>

            {/* Forgot Password Modal */}
            <Modal
                open={fpVisible}
                onCancel={() => {
                    setFpVisible(false);
                    setIdentifier("");
                }}
                footer={null}
                width={isCompact ? 'min(calc(100vw - 24px), 480px)' : 480}
                centered={!isCompact}
                styles={{
                    content: {
                        borderRadius: isCompact ? 16 : 24,
                        padding: 0,
                        border: `1px solid ${palette.border}`,
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                        margin: isCompact ? '12px auto' : undefined,
                        maxWidth: isCompact ? 'calc(100vw - 24px)' : undefined,
                    },
                    header: {
                        padding: 0,
                        borderBottom: 'none',
                    },
                    body: {
                        padding: isCompact ? '24px 20px' : 40,
                    },
                }}
                title={null}
                closeIcon={
                    <span
                        style={{
                            color: palette.textMuted,
                            fontSize: '18px',
                            padding: '8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            transition: 'all 0.2s',
                            cursor: 'pointer',
                        }}
                    >
                        ✕
                    </span>
                }
            >
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '16px',
                            }}
                        >
                            <KeyOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
                        </div>
                        <Title
                            level={3}
                            style={{
                                margin: 0,
                                marginBottom: '8px',
                                color: palette.textPrimary,
                                fontSize: '24px',
                                fontWeight: 700,
                            }}
                        >
                            {t('auth.forgotPassword.title')}
                        </Title>
                        <Text
                            style={{
                                color: palette.textSecondary,
                                fontSize: '14px',
                                lineHeight: 1.6,
                            }}
                        >
                            {t('auth.forgotPassword.subtitle')}
                        </Text>
                    </div>
                    <div>
                        <Input
                            prefix={<MailOutlined style={{ color: palette.textMuted }} />}
                            placeholder={t('auth.forgotPassword.emailOrLoginPlaceholder')}
                            size="large"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            onPressEnter={async () => {
                                if (!identifier.trim()) {
                                    message.warning(t('auth.forgotPassword.emailOrLoginRequired'));
                                    return;
                                }
                                setFpLoading(true);
                                try {
                                    await requestPasswordReset(identifier.trim());
                                    message.success(t('auth.forgotPassword.success'));
                                    setFpVisible(false);
                                    setIdentifier("");
                                } catch (e: any) {
                                    message.error(e?.response?.data?.message ?? t('auth.forgotPassword.error'));
                                } finally {
                                    setFpLoading(false);
                                }
                            }}
                            style={{
                                borderRadius: '12px',
                                padding: '12px 16px',
                                fontSize: '15px',
                                border: `1px solid ${palette.border}`,
                                marginBottom: '20px',
                                transition: 'all 0.2s',
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = palette.primary;
                                e.target.style.boxShadow = `0 0 0 3px ${palette.primary}1a`;
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = palette.border;
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <Button
                                type="primary"
                                size="large"
                                block
                                loading={fpLoading}
                                onClick={async () => {
                                    if (!identifier.trim()) {
                                        message.warning(t('auth.forgotPassword.emailOrLoginRequired'));
                                        return;
                                    }
                                    setFpLoading(true);
                                    try {
                                        await requestPasswordReset(identifier.trim());
                                        message.success(t('auth.forgotPassword.success'));
                                        setFpVisible(false);
                                        setIdentifier("");
                                    } catch (e: any) {
                                        message.error(e?.response?.data?.message ?? t('auth.forgotPassword.error'));
                                    } finally {
                                        setFpLoading(false);
                                    }
                                }}
                                style={{
                                    borderRadius: '12px',
                                    height: '48px',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    border: 'none',
                                    background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                                    boxShadow: `0 1px 2px rgba(45, 156, 201, 0.2)`,
                                }}
                            >
                                {fpLoading ? t('auth.forgotPassword.submitting') : t('auth.forgotPassword.submit')}
                            </Button>
                            <Button
                                type="text"
                                size="large"
                                block
                                onClick={() => {
                                    setFpVisible(false);
                                    setIdentifier("");
                                }}
                                style={{
                                    color: palette.textSecondary,
                                    fontWeight: 500,
                                }}
                            >
                                {t('auth.forgotPassword.cancel')}
                            </Button>
                        </Space>
                    </div>
                </Space>
            </Modal>

            {/* Modal — Renvoyer l'email d'activation */}
            <Modal
                open={resendVisible}
                onCancel={() => { setResendVisible(false); setResendEmail(""); }}
                footer={null}
                width={isCompact ? 'min(calc(100vw - 24px), 480px)' : 480}
                centered={!isCompact}
                styles={{
                    content: {
                        borderRadius: isCompact ? 16 : 24,
                        padding: 0,
                        border: `1px solid ${palette.border}`,
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                    },
                    body: { padding: isCompact ? '24px 20px' : 40 },
                }}
                title={null}
                closeIcon={<span style={{ color: palette.textMuted, fontSize: '18px', padding: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', cursor: 'pointer' }}>✕</span>}
            >
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ width: 56, height: 56, borderRadius: '50%', background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                            <MailOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
                        </div>
                        <Title level={3} style={{ margin: 0, marginBottom: '8px', color: palette.textPrimary, fontSize: '22px', fontWeight: 700 }}>
                            Renvoyer l'email d'activation
                        </Title>
                        <Text style={{ color: palette.textSecondary, fontSize: '14px', lineHeight: 1.6 }}>
                            Saisissez votre adresse email pour recevoir un nouveau lien d'activation.
                        </Text>
                    </div>
                    <div>
                        <Input
                            prefix={<MailOutlined style={{ color: palette.textMuted }} />}
                            placeholder="Votre adresse email"
                            size="large"
                            value={resendEmail}
                            onChange={(e) => setResendEmail(e.target.value)}
                            onPressEnter={handleResendSubmit}
                            style={{ borderRadius: '12px', padding: '12px 16px', fontSize: '15px', border: `1px solid ${palette.border}`, marginBottom: '20px' }}
                        />
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <Button
                                type="primary"
                                size="large"
                                block
                                loading={resendLoading}
                                onClick={handleResendSubmit}
                                style={{ borderRadius: '12px', height: '48px', fontSize: '15px', fontWeight: 600, border: 'none', background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})` }}
                            >
                                {resendLoading ? 'Envoi en cours...' : 'Envoyer'}
                            </Button>
                            <Button type="text" size="large" block onClick={() => { setResendVisible(false); setResendEmail(""); }} style={{ color: palette.textSecondary, fontWeight: 500 }}>
                                Annuler
                            </Button>
                        </Space>
                    </div>
                </Space>
            </Modal>

            <Footer
                style={{
                    background: 'transparent',
                    padding: isCompact ? '16px 12px max(16px, env(safe-area-inset-bottom))' : 24,
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <Text
                    style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '13px',
                    }}
                >
                    {t('auth.login.footer', { year: new Date().getFullYear(), version: appVersion || 'dev' })}
                </Text>
            </Footer>
        </Layout>
    );
};

export default Login;
